import React, {useState, useRef, DragEvent, ChangeEvent} from "react";
import {
  Box,
  Typography,
  Alert,
  Stack,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import {CloudUpload} from "@mui/icons-material";
import {getFileIcon, translate} from "@util";
import {CancelIcon, SaveIcon} from "@components/icons";
import {useThemeContext} from "@theme";
import {truncateSx} from "./calendar/util";

interface InputFileProps {
  maxSize?: number; // in bytes
  disabled?: boolean;
  error?: string;
  multiple?: boolean;
  loading: boolean;
  onConfirm: (files: File[]) => void;
}

const InputFile: React.FC<InputFileProps> = ({
  onConfirm,
  loading,
  maxSize = 10 * 1024 * 1024, // 10MB default
  disabled = false,
  error: externalError,
  multiple = false,
}) => {
  const {theme} = useThemeContext();

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [internalError, setInternalError] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const error = externalError || internalError;

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const validateFiles = (files: File[]): File[] => {
    const validFiles: File[] = [];
    let errorMessage = "";

    for (const file of files) {
      if (file.size > maxSize) {
        errorMessage = `File "${file.name}" troppo grande. Dimensione massima: ${formatFileSize(maxSize)}`;
        break;
      }
      validFiles.push(file);
    }

    if (errorMessage) {
      setInternalError(errorMessage);
      return [];
    }

    return validFiles;
  };

  const handleFileSelection = (addedFiles: File[]): void => {
    setInternalError("");

    const validFiles = validateFiles(addedFiles);
    if (validFiles.length === 0) return;

    if (multiple) {
      const newFiles = [...files, ...validFiles];
      setFiles(newFiles);
    } else {
      const singleFile = validFiles[0];
      setFiles([singleFile]);
    }
  };

  const handleButtonClick = (): void => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelection(Array.from(files));
    }
  };

  const handleRemoveFile = (index?: number): void => {
    if (multiple && typeof index === "number") {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
      setInternalError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      // Single file mode or remove all
      setFiles([]);
      setInternalError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirm = () => {
    onConfirm(files);
    setFiles([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const renderFile = (file: File, index: number) => {
    return (
      <Stack
        key={`${file.name}-${index}`}
        direction="row"
        gap={2}
        alignItems="center"
        sx={{}}>
        <Box sx={{"& svg": {fontSize: "1.5rem"}}}>{getFileIcon(file.type)}</Box>
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" sx={truncateSx}>
            {file.name}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign={"center"}
          sx={{minWidth: 60}}>
          {formatFileSize(file.size)}
        </Typography>
        <IconButton
          onClick={() => handleRemoveFile(multiple ? index : undefined)}
          size="small">
          <CancelIcon fontSize="inherit" />
        </IconButton>
      </Stack>
    );
  };

  const renderfiles = () => {
    if (files.length === 0) return null;

    if (multiple) {
      return (
        <Stack gap={1} px={2}>
          {files.map((file, index) => renderFile(file, index))}
        </Stack>
      );
    } else {
      return <Box>{renderFile(files[0], 0)}</Box>;
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        border: isDragging
          ? `1px solid ${theme.palette.primary.dark}`
          : `1px solid ${theme.palette.divider}`,
        backgroundColor: isDragging
          ? theme.colors.primaryContainer
          : disabled
            ? theme.colors.grey
            : theme.colors.secondaryContainer,
      }}>
      <Stack position="relative" flex={1}>
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleButtonClick}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
        />
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          style={{display: "none"}}
          disabled={disabled}
          multiple={multiple}
        />

        <Box
          sx={{
            p: 1,
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}>
          {loading ? (
            <CircularProgress sx={{my: "5px"}} />
          ) : (
            <>
              <CloudUpload
                color={isDragging ? "primary" : "action"}
                sx={{
                  fontSize: 30,
                }}
              />
              <Typography variant="body2">
                {translate(
                  `common.label.${isDragging ? "releaseHere" : "clickOrDrag"}`,
                )}
              </Typography>
            </>
          )}

          {multiple && files.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {files.length} file selezionati
            </Typography>
          )}
        </Box>
      </Stack>

      {renderfiles()}

      {error && (
        <Alert severity="error" sx={{mt: 2}}>
          {error}
        </Alert>
      )}

      {files.length > 0 && (
        <Stack direction={"row"} gap={2} justifyContent={"end"} p={2} flex={1}>
          <Button onClick={() => setFiles([])}>
            {translate("common.action.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            startIcon={<SaveIcon />}
            variant="contained">
            {translate("common.action.confirm")}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export {InputFile};
